// React imports
import React from 'react';

// Generic imports
import _ from 'lodash';

// MUI imports
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { grey } from '@mui/material/colors';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';

// Local import
import { calcVolumeWeight, calcWeight, calcChargeableWeight, calcWeightOrMeasurment } from '../CalcWeight.js';
import { getProductDescription } from '../dashboard/utils/product.js';

function ProductDescription(props) {
    const { product, transportationMethod, allProducts, organization } = props;

    if (!product?.type) return null;

    const description = getProductDescription({ product, transportationMethod, allProducts, organization });

    return (
        <Box sx={{ whiteSpace: 'pre-line' }}>
            {description}{product.dangerousMSDS ? <span>, MSDS: <a target='_blank' href={product.dangerousMSDS}>Download</a></span> : ''}
        </Box>
    )
}

function ProductsSummary(props) {
    const { products, transportationMethod, organization, noBorder } = props;

    if (!products.find(p => p.type)) return null;

    // A land quote has no summary of its own, and it can now carry cargo that does: air freight
    // rides the trucking leg, and groupage has always been able to. Neither branch below matched,
    // so a land quote fell out of this function showing nothing at all — no totals, no weights, no
    // volume.
    //
    // Each kind of cargo is handed to the summary that already knows how to measure it, rather
    // than that measuring being written out a second time here. Both are shown when a land quote
    // carries both. Air and sea quotes do not reach this and are untouched.
    if (transportationMethod === 'land') {
        const airProducts = products.filter(p => p.type === 'Air freight' || p.type === 'Air Freight');
        const groupageProducts = products.filter(p => p.type === 'LCL'
            || p.type === 'By Dimensions or Weight (sea)'
            || p.type === 'By Volume or Weight (sea)');

        if (airProducts.length === 0 && groupageProducts.length === 0) return null;

        return (
            <>
                {airProducts.length > 0 && (
                    <ProductsSummary products={airProducts} transportationMethod='air' organization={organization} noBorder={noBorder} />
                )}
                {groupageProducts.length > 0 && (
                    <ProductsSummary products={groupageProducts} transportationMethod='sea' organization={organization} noBorder={noBorder} />
                )}
            </>
        );
    }

    if (transportationMethod === 'air') {
        const totalCW = _(products)
            .filter(p => !!p.type)
            .map(p => calcChargeableWeight({ product: p, transportationMethod, allProducts: products, round: organization.airRoundChargeableWeight }))
            .groupBy(s => s.postfix)
            .mapValues(v => _.sumBy(v, v2 => v2.val))
            .map((v,k) => `${v.toLocaleString()} ${k}`)
            .value()
            .join(',');

        const totalWeight = _(products)
            .filter(p => !!p.type)
            .map(p => calcWeight({ product: p }))
            .groupBy(s => s.postfix)
            .mapValues(v => _.sumBy(v, v2 => v2.val))
            .map((v,k) => `${v.toLocaleString()} ${k}`)
            .value()
            .join(',');

        const totalVolume = _(products)
            .filter(p => !!p.type)
            .map(p => calcVolumeWeight({ product: p, transportationMethod: 'air', organization, round: organization.airRoundChargeableWeight }))
            .groupBy(s => s.postfix)
            .mapValues(v => _.sumBy(v, v2 => v2.val))
            .map((v,k) => `${v.toLocaleString()} ${k}`)
            .value()
            .join(',');

        // A package entered By Chargeable Weight is charged at the figure typed on it, and its
        // gross weight only joins the total weight. The three totals then cannot be reconciled by
        // eye - the chargeable weight is not the larger of the other two, and the missing gross
        // weight reads as cargo that got lost. An info icon on the chargeable weight says where it
        // went, for the reader who goes looking; every other quote shows the line it always had.
        const cwPackages = _(products)
            .filter(p => p.type === 'Air freight')
            .flatMap(p => (p.calculations || [])
                .filter(c => c.method === 'chargeable-weight')
                .map(c => ({
                    postfix: p.kgOrLbs || 'kg',
                    quantity: parseInt(c.quantity) || 1,
                    chargeableWeight: parseFloat(c.chargeableWeight) || 0,
                    weight: parseFloat(c.weight) || 0,
                })))
            .value();

        const totalForCWPackages = (getter) => _(cwPackages)
            .groupBy(c => c.postfix)
            .mapValues(v => _.sumBy(v, getter))
            .map((v, k) => `${v.toLocaleString()} ${k}`)
            .value()
            .join(', ');

        const enteredCW = totalForCWPackages(c => c.chargeableWeight * c.quantity);
        const enteredGross = totalForCWPackages(c => c.weight * c.quantity);
        const hasEnteredGross = _.sumBy(cwPackages, c => c.weight * c.quantity) > 0;
        const packageCount = _.sumBy(cwPackages, c => c.quantity);

        const cwExplanation = (
            <>
                {packageCount === 1 ? '1 package is' : `${packageCount} packages are`} priced By Chargeable Weight.
                The {enteredCW} typed on {packageCount === 1 ? 'it' : 'them'} is charged as-is.
                {hasEnteredGross && <> {packageCount === 1 ? 'Its' : 'Their'} {enteredGross} gross weight counts towards the total weight, but not towards the chargeable weight.</>}
            </>
        );

        return (
            <Box sx={{ paddingTop: '6px', marginTop: '6px', borderTop: noBorder ? '' : `1px solid ${grey[300]}` }}>
                Total products: <b>{_.sumBy(products.filter(p => p.type), p => {
                    if (p.type === 'Air freight') {
                        return _.sumBy(p.calculations || [], c => parseInt(c.quantity || 1));
                    }
                    return parseInt(p.quantity || 1);
                })}</b>. Total weight: <b>{totalWeight}</b>. Total volume weight: <b>{totalVolume}</b>. Total chargeable weight: <b>{totalCW}</b>
                {cwPackages.length > 0 && (
                    <Tooltip title={cwExplanation} arrow>
                        <sup>
                            {/* Sized here, not only by Font Awesome's stylesheet: the quote page renders this
                                summary on the server, where that stylesheet isn't loaded, and an unsized SVG
                                stretched to the full page width and pushed the quote out of view. */}
                            <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '11px', width: '11px', height: '11px', marginLeft: '4px', cursor: 'help', color: grey[600] }} />
                        </sup>
                    </Tooltip>
                )}
            </Box>
        )
    }

    if (transportationMethod === 'sea') {

        const lclProducts = products.filter(p => p.type === 'LCL');
        const wmLegacyProducts = products.filter(p => p.type === 'By Dimensions or Weight (sea)' || p.type === 'By Volume or Weight (sea)');
        const hasLcl = lclProducts.length > 0 || wmLegacyProducts.length > 0;
        if (!hasLcl) return null;

        // Sum packages (calculation quantities) across LCL products; legacy W/M products count as their quantity
        const totalPackages = _.sumBy(lclProducts, p => _.sumBy(p.calculations || [], c => parseInt(c.quantity || 1)))
            + _.sumBy(wmLegacyProducts, p => parseInt(p.quantity || 1));

        // Total weight across LCL + legacy W/M
        const totalWeightStr = _([...lclProducts, ...wmLegacyProducts])
            .map(p => ({ val: (() => {
                if (p.type === 'LCL') return _.sumBy(p.calculations || [], c => (c.weight || 0) * (c.quantity || 1));
                return (p.weight || 0) * (p.quantity || 1);
            })(), postfix: p.kgOrLbs === 'lbs' ? 'lbs' : 'kg' }))
            .groupBy(s => s.postfix)
            .mapValues(v => _.sumBy(v, v2 => v2.val))
            .map((v, k) => `${v.toLocaleString()} ${k}`)
            .value()
            .join(', ');

        // Total volume: cbm/cft grouped by unit
        const totalVolumeStr = _([...lclProducts, ...wmLegacyProducts])
            .map(p => {
                if (p.type === 'LCL') {
                    const divisor = (p.cmOrInches || 'cm') === 'cm' ? 1000000 : 1728;
                    const postfix = (p.cmOrInches || 'cm') === 'cm' ? 'cbm' : 'cft';
                    const val = _.sumBy(p.calculations || [], c => {
                        const qty = c.quantity || 1;
                        if (c.method === 'dimensions') return (c.length || 0) * (c.width || 0) * (c.height || 0) / divisor * qty;
                        return (c.volume || 0) * qty;
                    });
                    return { val, postfix };
                }
                const vol = p.volume ? (p.volume * (p.quantity || 1)) : 0;
                return { val: vol, postfix: 'cbm' };
            })
            .groupBy(s => s.postfix)
            .mapValues(v => _.sumBy(v, v2 => v2.val))
            .map((v, k) => `${v.toLocaleString()} ${k}`)
            .value()
            .join(', ');

        // Total W/M across LCL + legacy W/M (max of weight-in-ton vs volume-in-cbm per product, summed)
        const totalWMStr = _([...lclProducts, ...wmLegacyProducts])
            .map(p => calcWeightOrMeasurment({ product: p, transportationMethod, allProducts: products }))
            .compact()
            .groupBy(s => s.postfix)
            .mapValues(v => _.sumBy(v, v2 => v2.val))
            .map((v, k) => `${v.toLocaleString()} ${k}`)
            .value()
            .join(', ');

        return (
            <Box sx={{ paddingTop: '6px', marginTop: '6px', borderTop: noBorder ? '' : `1px solid ${grey[300]}` }}>
                Total LCL packages: <b>{totalPackages}</b>. Total weight: <b>{totalWeightStr}</b>. Total volume: <b>{totalVolumeStr}</b>. Total W/M: <b>{totalWMStr}</b>
            </Box>
        )
    }
}

export { ProductsSummary, ProductDescription };
